import { AppConfig } from '@ltvco/refresh-lib/ctx';
import { useServiceToken, useSnackbar } from '@ltvco/refresh-lib/v1';
import { useQuery } from '@ltvco/refresh-lib/vendors';
import { useState, useEffect, useRef, useContext } from 'react';

import {
  type OpinionsResponse,
  type PACERResponse,
  type OralArgumentsResponse,
  type JudgeSearchResponse,
  type ClassActionSearchResponse,
  type CourtTypeParam,
  type CourtCaseCluster,
  type CourtOpinion,
  type OralArgumentResult,
  type RawOralArgumentDetailResponse,
} from '../../types';
import {
  buildCourtSearchParams,
  getOrderByParam,
  getUIOrderByValue,
  normalizeAdvancedFilters,
  processExactMatchQuery,
} from '../../utils';
import { type ApiFilters, prepareQueryForCacheKey } from '../utils/filterUtils';

export const API_CONFIG = {
  BASE_URL: '/oram',
  CACHE_TIME: 10 * 60 * 1000,
  STALE_TIME: 2 * 60 * 1000,
  MAX_RETRIES: 2,
} as const;

export const DEFAULT_QUERY_OPTIONS = {
  staleTime: API_CONFIG.STALE_TIME,
  cacheTime: API_CONFIG.CACHE_TIME,
  refetchOnWindowFocus: false,
} as const;

export const ERROR_MESSAGES = {
  TOKEN_UNAVAILABLE:
    'Service token not available. Please wait for authentication to complete.',
  TOKEN_REFRESH_FAILED: 'Failed to refresh service token',
  OPINIONS_SEARCH_FAILED: 'Failed to search court opinions',
  CASES_SEARCH_FAILED: 'Failed to search court cases',
  GENERAL_ERROR:
    'Something went wrong while loading results. Please try again.',
} as const;

export const makeAuthenticatedGetRequest = async <T>(
  url: string,
  token: string
): Promise<T> => {
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`API error: ${response.status} ${response.statusText}`);
  }

  return response.json();
};

export const resetSearchRefAfterDelay = (
  ref: React.MutableRefObject<boolean>,
  delay: number = 100
): void => {
  setTimeout(() => {
    ref.current = false;
  }, delay);
};

export const createDelay = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

export const createSearchFunction = <
  T extends
    | OpinionsResponse
    | PACERResponse
    | OralArgumentsResponse
    | JudgeSearchResponse
>(
  searchType: CourtTypeParam,
  makeApiCall: (apiCall: (token: string) => Promise<T>) => Promise<T>
) => {
  return async (
    query: string,
    orderBy: string = 'score desc',
    additionalParams: Record<string, string | number | boolean> = {}
  ): Promise<T> => {
    return makeApiCall(async (token: string) => {
      const searchParams = buildCourtSearchParams(
        searchType,
        query,
        orderBy,
        additionalParams
      );
      const url = `${API_CONFIG.BASE_URL}/legal/search/?${searchParams}`;
      return makeAuthenticatedGetRequest<T>(url, token);
    });
  };
};

export const useBaseCourtApi = () => {
  const {
    data: tokenData,
    refetch: refetchToken,
    isLoading: isTokenLoading,
  } = useServiceToken('oram');
  const { logError } = useContext(AppConfig);

  const makeApiCall = <T>(
    apiCall: (token: string) => Promise<T>,
    retryCount = 0
  ): Promise<T> => {
    if (!tokenData?.token) {
      throw new Error(ERROR_MESSAGES.TOKEN_UNAVAILABLE);
    }
    return apiCall(tokenData.token).catch(async (error: unknown) => {
      if (
        error instanceof Error &&
        error.message?.includes('401') &&
        retryCount === 0
      ) {
        try {
          await refetchToken();
          await createDelay(100);
          return makeApiCall(apiCall, retryCount + 1);
        } catch {
          throw new Error(ERROR_MESSAGES.TOKEN_REFRESH_FAILED);
        }
      }
      throw error;
    });
  };

  const fetchPaginationPage = <
    T extends
      | OpinionsResponse
      | PACERResponse
      | OralArgumentsResponse
      | JudgeSearchResponse
      | ClassActionSearchResponse
  >(
    url: string,
    endpoint: string = 'search'
  ): Promise<T> => {
    return makeApiCall(async (token: string) => {
      const urlObj = new URL(url);
      const oramUrl = `${API_CONFIG.BASE_URL}/legal/${endpoint}/${urlObj.search}`;
      return makeAuthenticatedGetRequest<T>(oramUrl, token);
    });
  };

  const getDocketEntries = async (
    docket: string,
    page?: number
  ): Promise<any> => {
    return makeApiCall(async (token: string) => {
      let url = `${API_CONFIG.BASE_URL}/legal/docket-entries/?docket=${docket}&order_by=-recap_sequence_number`;

      if (page) {
        url += `&page=${page}`;
      }

      const data = await makeAuthenticatedGetRequest<any>(url, token);
      return data;
    });
  };

  const getDocket = async (id: string): Promise<any | null> => {
    return makeApiCall(async (token: string) => {
      const url = `${API_CONFIG.BASE_URL}/legal/dockets/?id=${id}`;
      const data = await makeAuthenticatedGetRequest<any>(url, token);
      return data.results?.[0] || null;
    });
  };

  const getCourt = async (id: string): Promise<any> => {
    return makeApiCall(async (token: string) => {
      const url = `${API_CONFIG.BASE_URL}/legal/courts/?id=${id}`;
      const data = await makeAuthenticatedGetRequest<any>(url, token);
      return data.results?.[0] || null;
    });
  };

  const validateId = (id: string, type: string = 'ID'): string => {
    const trimmedId = id.trim();

    if (!trimmedId) {
      throw new Error(`${type} cannot be empty`);
    }

    if (!/^[a-zA-Z0-9\-_.:]+$/.test(trimmedId)) {
      throw new Error(`${type} contains invalid characters`);
    }

    return trimmedId;
  };

  const validateEndpoint = (endpoint: string): string => {
    const trimmedEndpoint = endpoint.trim();

    if (!trimmedEndpoint) {
      throw new Error('Endpoint cannot be empty');
    }

    const allowedEndpoints = [
      'attorneys',
      'parties',
      'docket-entries',
      'search',
      'clusters',
      'opinions',
      'dockets',
      'courts',
    ];

    if (!allowedEndpoints.includes(trimmedEndpoint)) {
      throw new Error(`Invalid endpoint: ${trimmedEndpoint}`);
    }

    return trimmedEndpoint;
  };

  const isValidNextUrl = (url: string): boolean => {
    try {
      const urlObj = new URL(url);

      if (urlObj.protocol !== 'https:') {
        return false;
      }

      if (
        url.includes('<') ||
        url.includes('>') ||
        url.includes('"') ||
        url.includes("'")
      ) {
        return false;
      }
    } catch {
      return false;
    }
    return true;
  };

  const getAllPaginatedData = async <
    T,
    R extends { results: T[]; next: string | null }
  >(
    endpoint: string,
    docketId: string
  ): Promise<T[]> => {
    return makeApiCall(async (token: string) => {
      const validatedEndpoint = validateEndpoint(endpoint);
      const validatedDocketId = validateId(docketId, 'Docket ID');

      const maxPages = 25;
      let allItems: T[] = [];

      const searchParams = new URLSearchParams({ docket: validatedDocketId });
      let nextUrl: string | null = `${
        API_CONFIG.BASE_URL
      }/legal/${validatedEndpoint}?${searchParams.toString()}`;
      let pageCount = 0;

      while (pageCount < maxPages && nextUrl) {
        const response: R = await makeAuthenticatedGetRequest<R>(
          nextUrl,
          token
        );
        allItems = [...allItems, ...response.results];
        pageCount++;

        if (response.next) {
          if (isValidNextUrl(response.next)) {
            const urlObj = new URL(response.next);
            nextUrl = `${API_CONFIG.BASE_URL}/legal/${validatedEndpoint}${urlObj.search}`;
          } else {
            nextUrl = null;
          }
        } else {
          nextUrl = null;
        }
      }

      if (pageCount >= maxPages) {
        logError(
          `getAllPaginatedData for ${endpoint} reached maximum page limit (${maxPages}) for docket ${docketId}`,
          new Error(`Pagination limit exceeded for ${endpoint}`)
        );
      }

      return allItems;
    });
  };

  const getAllAttorneys = async (docketId: string): Promise<any[]> => {
    return getAllPaginatedData<any, any>('attorneys', docketId);
  };

  const getAllParties = async (docketId: string): Promise<any[]> => {
    return getAllPaginatedData<any, any>('parties', docketId);
  };

  const getCluster = async (id: string): Promise<CourtCaseCluster | null> => {
    return makeApiCall(async (token: string) => {
      const url = `${API_CONFIG.BASE_URL}/legal/clusters/?id=${id}`;
      const data = await makeAuthenticatedGetRequest<any>(url, token);
      return data.results?.[0] || null;
    });
  };

  const getClusterOpinions = async (
    clusterId: string
  ): Promise<CourtOpinion[]> => {
    return makeApiCall(async (token: string) => {
      const url = `${API_CONFIG.BASE_URL}/legal/opinions/?cluster=${clusterId}`;
      const data = await makeAuthenticatedGetRequest<any>(url, token);
      return data.results || [];
    });
  };

  /**
   * Fetches oral argument details by ID from audio endpoint
   */
  const getOralArgument = async (
    id: string
  ): Promise<OralArgumentResult | null> => {
    return makeApiCall(async (token: string) => {
      const validatedId = encodeURIComponent(
        validateId(id, 'Oral argument ID')
      );
      const url = `${API_CONFIG.BASE_URL}/legal/audio/?id=${validatedId}`;
      const data = await makeAuthenticatedGetRequest<{
        results: RawOralArgumentDetailResponse[];
      }>(url, token);
      const rawResult = data.results?.[0];

      if (!rawResult) {
        return null;
      }

      let docketId = rawResult.docket_id;
      if (!docketId && rawResult.docket) {
        const docketMatch = rawResult.docket.match(/\/dockets\/(\d+)\//);
        if (docketMatch) {
          docketId = parseInt(docketMatch[1], 10);
        }
      }

      const normalized: OralArgumentResult = {
        absolute_url: rawResult.absolute_url || '',
        caseName: rawResult.case_name || '',
        case_name_full: rawResult.case_name_full || '',
        court: rawResult.court || '',
        court_citation_string: rawResult.court_citation_string || '',
        court_id: rawResult.court_id || '',
        dateArgued: rawResult.date_argued || '',
        dateReargued: rawResult.date_reargued || null,
        dateReargumentDenied: rawResult.date_reargument_denied || null,
        docketNumber: rawResult.docket_number || '',
        docket_id: docketId || 0,
        download_url: rawResult.download_url || '',
        duration: rawResult.duration || 0,
        file_size_mp3: rawResult.file_size_mp3 || 0,
        id: rawResult.id || 0,
        judge: rawResult.judge || '',
        judges: rawResult.judges || rawResult.judge || '',
        local_path: rawResult.local_path || rawResult.local_path_mp3 || '',
        stt_transcript: rawResult.stt_transcript || null,
        meta: {
          timestamp: rawResult.meta?.timestamp || rawResult.date_created || '',
          date_created:
            rawResult.meta?.date_created || rawResult.date_created || '',
          score: {
            bm25: rawResult.meta?.score?.bm25 ?? 0,
          },
        },
        pacer_case_id: rawResult.pacer_case_id || '',
        panel_ids: Array.isArray(rawResult.panel)
          ? rawResult.panel
          : rawResult.panel_ids || [],
        sha1: rawResult.sha1 || '',
        snippet: rawResult.snippet || '',
        source: rawResult.source || '',
      };

      if (!normalized.court && normalized.docket_id) {
        try {
          const docket = await getDocket(normalized.docket_id.toString());
          if (docket?.court_id) {
            const court = await getCourt(docket.court_id);
            if (court) {
              normalized.court = court.full_name || court.short_name || '';
              normalized.court_id = court.id || normalized.court_id;
              normalized.court_citation_string =
                court.citation_string || normalized.court_citation_string;
              normalized.docketNumber =
                docket.docket_number || normalized.docketNumber;
              normalized.dateArgued =
                docket.date_argued || normalized.dateArgued;
            }
          }
        } catch (error) {
          logError(
            'Failed to fetch court info for oral argument',
            error as Error
          );
        }
      }

      return normalized;
    });
  };

  const searchCaseLaw = async (
    query: string,
    orderBy: string = 'score desc',
    additionalParams: Record<string, string | number | boolean> = {}
  ): Promise<OpinionsResponse> => {
    return makeApiCall(async (token: string) => {
      const searchParams = buildCourtSearchParams(
        'o',
        query,
        orderBy,
        additionalParams
      );
      const url = `${API_CONFIG.BASE_URL}/legal/search/?${searchParams}`;
      return makeAuthenticatedGetRequest<OpinionsResponse>(url, token);
    });
  };

  return {
    makeApiCall,
    fetchPaginationPage,
    getDocketEntries,
    getDocket,
    getCourt,
    getOralArgument,
    getAllAttorneys,
    getAllParties,
    getCluster,
    getClusterOpinions,
    searchCaseLaw,
    isLoading: isTokenLoading,
    tokenData,
    logError,
  };
};

/**
 * Configuration for the generic court search hook
 */
interface CourtSearchConfig {
  searchType: CourtTypeParam;
  queryKeyPrefix: string;
  initialQuery?: string;
  exactMatch?: boolean;
}

/**
 * Generic hook for court search functionality
 * Handles all common logic for Cases, Opinions, and Oral Arguments searches
 */
export const useCourtSearch = <
  T extends
    | OpinionsResponse
    | PACERResponse
    | OralArgumentsResponse
    | JudgeSearchResponse
>(
  config: CourtSearchConfig
) => {
  const {
    searchType,
    queryKeyPrefix,
    initialQuery: initialQueryProp = '',
    exactMatch: exactMatchProp = true,
  } = config;
  const isOralArguments = searchType === 'oa';
  const isJudgeSearch = searchType === 'p';

  const [sortOption, setSortOption] = useState('score_desc');
  const [currentQuery, setCurrentQuery] = useState('');
  const [currentFilters, setCurrentFilters] = useState<ApiFilters>({});
  const [results, setResults] = useState<T | null>(null);
  const [searchExecuted, setSearchExecuted] = useState(false);
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);
  const [currentExactMatch, setCurrentExactMatch] = useState(exactMatchProp);

  const isSearchingRef = useRef(false);
  const isUpdatingStateRef = useRef(false);

  const { enqueueSnackbar } = useSnackbar();
  const { logError } = useContext(AppConfig);
  const {
    makeApiCall,
    isLoading: isTokenLoading,
    tokenData,
  } = useBaseCourtApi();

  const searchFunction = createSearchFunction<T>(searchType, makeApiCall);

  const hasAnyFilters = Object.keys(currentFilters).length > 0;
  const hasValidSearchParams = Boolean(currentQuery?.trim()) || hasAnyFilters;
  const shouldExecuteQuery =
    searchExecuted && hasValidSearchParams && !isUpdatingStateRef.current;

  const handleSearchError = (error: unknown, context: string) => {
    const isExpectedError =
      error instanceof Error &&
      error.message?.includes('Search query is required');

    if (!isExpectedError) {
      logError(`Court search error in ${context}`, error as Error);
      enqueueSnackbar(ERROR_MESSAGES.GENERAL_ERROR, { variant: 'error' });
    }
  };

  const executeSearch = async (): Promise<T> => {
    const orderBy = getOrderByParam(sortOption, isOralArguments, isJudgeSearch);
    const processedQuery = currentQuery.trim()
      ? processExactMatchQuery(currentQuery, currentExactMatch)
      : currentQuery;

    return searchFunction(processedQuery, orderBy, currentFilters);
  };

  const filtersKey = hasAnyFilters
    ? JSON.stringify(currentFilters)
    : 'no-filters';
  const queryKeyQuery = prepareQueryForCacheKey(
    currentQuery,
    currentExactMatch
  );
  const queryKey = [queryKeyPrefix, queryKeyQuery, sortOption, filtersKey];

  const searchQuery = useQuery(queryKey, executeSearch, {
    enabled: shouldExecuteQuery && !isTokenLoading && !!tokenData?.token,
    staleTime: API_CONFIG.STALE_TIME,
    cacheTime: API_CONFIG.CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: (failureCount: number, error: unknown) => {
      const isExpectedError =
        error instanceof Error &&
        error.message?.includes('Search query is required');
      return !isExpectedError && failureCount < API_CONFIG.MAX_RETRIES;
    },
    onSuccess: (data: T) => {
      setResults(data);
      setSearchExecuted(false);
    },
    onError: (error: unknown) => {
      handleSearchError(error, 'search query execution');
      setSearchExecuted(false);
      setResults(null);
    },
  });

  // Sync exactMatch prop with internal state
  useEffect(() => {
    if (currentExactMatch !== exactMatchProp) {
      setCurrentExactMatch(exactMatchProp);
      if (currentQuery.trim() && searchQuery.data) {
        searchQuery.refetch();
      }
    }
  }, [exactMatchProp, currentQuery, currentExactMatch, searchQuery]);

  // Initialize search from URL query param
  useEffect(() => {
    if (initialQueryProp?.trim()) {
      isUpdatingStateRef.current = true;
      setCurrentQuery(initialQueryProp);
      setTimeout(() => {
        isUpdatingStateRef.current = false;
        setSearchExecuted(true);
      }, 0);
    }
  }, [initialQueryProp]);

  // Sync results from react-query
  useEffect(() => {
    if (searchQuery.data && !searchQuery.isLoading) {
      setResults(searchQuery.data);
    }
  }, [searchQuery.data, searchQuery.isLoading]);

  const triggerCombinedSearch = (
    query: string,
    filters: Record<string, unknown> = {}
  ) => {
    isSearchingRef.current = true;
    isUpdatingStateRef.current = true;

    const apiReadyFilters = normalizeAdvancedFilters(
      filters,
      isOralArguments,
      isJudgeSearch
    );
    setCurrentFilters(apiReadyFilters as ApiFilters);

    // Sync sort option from filters (convert API format to UI format)
    if (
      apiReadyFilters.order_by &&
      typeof apiReadyFilters.order_by === 'string'
    ) {
      const uiOrderBy = getUIOrderByValue(
        apiReadyFilters.order_by,
        isOralArguments,
        isJudgeSearch
      );
      if (uiOrderBy !== sortOption) {
        setSortOption(uiOrderBy);
      }
    }

    setCurrentQuery(query);
    setTimeout(() => {
      isUpdatingStateRef.current = false;
      setSearchExecuted(true);
    }, 0);
    resetSearchRefAfterDelay(isSearchingRef);
  };

  const updateResults = (newResults: T) => {
    setResults(newResults);
    setIsPaginationLoading(false);
  };

  const isSearching = searchExecuted && searchQuery.isLoading;
  const hasResults = !!results;
  const isExpectedQueryError =
    searchQuery.error instanceof Error &&
    searchQuery.error.message?.includes('Search query is required');

  return {
    results,
    searchQuery: currentQuery,
    sortOption,

    isSearching,
    isAnyLoading: isSearching || isPaginationLoading,
    hasResults,
    hasError: !!searchQuery.error,
    generalError:
      searchQuery.error && !isExpectedQueryError
        ? ERROR_MESSAGES.GENERAL_ERROR
        : null,
    isTokenLoading,
    isPaginationLoading,

    triggerCombinedSearch,
    setSortOption,
    updateResults,
    setPaginationLoading: setIsPaginationLoading,
    setSearchExecuted,

    error: searchQuery.error,
    data: searchQuery.data,
    refetch: searchQuery.refetch,
  };
};
